// src/shared/leave-form.js - what ALGT's paper "Leave Application Form" collects, as one rule (Matt, 19 Oct 2026).
//
// Zero dependencies, on purpose: the Worker validates with it (src/worker/leave-requests.js validateRequest) and the
// two forms that raise a request (the till, src/leaveRequests; admin, src/leaveRequestsAdmin) draw their lists and
// check their fields with the SAME code, so the screen and the server can never disagree about what is required.
//
// THE FIELDS (in the paper form's order):
//   - the kind of holiday: ANNUAL leave or EMERGENCY leave. Emergency leave comes OFF THE ANNUAL LEAVE BALANCE
//     (Matt), so it is booked exactly like annual leave (holiday rows, calendar days); only the request remembers
//     which it was. At most 15 days. Proof of the emergency goes to HR (no upload on the till).
//   - "Request my air ticket": annual leave only. HR works out who is eligible; Shift only passes the tick on.
//   - two contact numbers while on leave: local and home town. Required for holiday (annual and emergency) and APL,
//     optional for TOIL. Phone-like text only: digits, spaces, + and -, at most 20 characters.
//   - the reason for leave: required for every request (it is stored in leave_requests.note, the old free-text note,
//     so an old request's note still reads as it did).
//   - company property to return before leave: a tick list, "None" when nothing is ticked (the usual answer for
//     store staff). Optional for TOIL.
// Leave encashment is NOT here (Matt: warehouse staff only, and they do not use Shift).
//
// WHERE IT IS KEPT. No migration: the fields ride in leave_requests.booked_days as ONE entry with action 'form'
// (the same trick as the HR outcome entries, action 'hr'). Every reader of booked_days that means "the days" takes
// only action 'booked', so this entry changes nothing else. These details are PRIVATE: admin and the HR email only,
// never the shared till (tillView in src/worker/leave-requests.js has a fixed list of keys).

export const FORM_VERSION = 1;
export const LEAVE_KINDS = ['annual', 'emergency'];
export const EMERGENCY_MAX_DAYS = 15;
export const PHONE_MAX = 20;
export const REASON_MAX = 500;
export const PROPERTY_OTHER_MAX = 100;
export const PROPERTY_ITEMS = [
  { key: 'sim', label: 'SIM' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'tab', label: 'Tab' },
  { key: 'laptop', label: 'Laptop' },
  { key: 'car_key', label: 'Car key' },
  { key: 'fuel_card', label: 'Fuel card' },
  { key: 'car_registration', label: 'Car registration' },
  { key: 'other', label: 'Other' },
];
const PROPERTY_KEYS = PROPERTY_ITEMS.map((p) => p.key);
export const EMERGENCY_PROOF_TEXT = 'Proof of the emergency must be given to HR.';

const str = (v) => String(v == null ? '' : v).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();

// Digits, spaces, + and -, at most 20 characters, at least one digit.
export function isPhoneLike(v) {
  const s = str(v);
  return s.length > 0 && s.length <= PHONE_MAX && /^[0-9+\-\s]+$/.test(s) && /\d/.test(s);
}
// Holiday (annual and emergency) and APL need both contact numbers and the property list; TOIL does not.
export function needsContacts(leaveType) { return leaveType === 'holiday' || leaveType === 'apl'; }

// body: what the form sent. leaveType: holiday | toil | apl (already checked). days: calendar days asked for.
// opts.localFallback: the staff record's phone, used when the local number is left empty.
// -> { error } or { form, reason }. form is the booked_days entry (action 'form').
export function validateLeaveForm(body, leaveType, days, opts) {
  const b = body || {};
  const o = opts || {};
  const reason = str(b.reason != null && str(b.reason) !== '' ? b.reason : b.note).slice(0, REASON_MAX);
  if (!reason) return { error: 'Write the reason for the leave.' };

  let kind = null;
  if (leaveType === 'holiday') {
    kind = String(b.leave_kind || 'annual').trim().toLowerCase();
    if (LEAVE_KINDS.indexOf(kind) === -1) return { error: 'Choose annual leave or emergency leave.' };
    if (kind === 'emergency' && Number(days) > EMERGENCY_MAX_DAYS) {
      return { error: 'Emergency leave can be at most ' + EMERGENCY_MAX_DAYS + ' days. Ask for annual leave for the rest.' };
    }
  }
  const ticket = kind === 'annual' && (b.ticket === true || b.ticket === 'true' || b.ticket === 1 || b.ticket === '1' || b.ticket === 'on');

  let local = str(b.contact_local);
  let localFromRecord = false;
  if (!local && o.localFallback && isPhoneLike(o.localFallback)) { local = str(o.localFallback); localFromRecord = true; }
  const home = str(b.contact_home);
  const required = needsContacts(leaveType);
  if (required && !local) return { error: 'Add a local contact number for while you are on leave.' };
  if (required && !home) return { error: 'Add a home-town contact number for while you are on leave.' };
  if ((local && !isPhoneLike(local)) || (home && !isPhoneLike(home))) {
    return { error: 'A contact number can only have digits, spaces, + and -, up to ' + PHONE_MAX + ' characters.' };
  }

  const raw = Array.isArray(b.property) ? b.property : (b.property ? String(b.property).split(',') : []);
  let property = [];
  raw.map((x) => String(x || '').trim().toLowerCase()).forEach((k) => {
    if (PROPERTY_KEYS.indexOf(k) !== -1 && property.indexOf(k) === -1) property.push(k);
  });
  property = PROPERTY_KEYS.filter((k) => property.indexOf(k) !== -1);   // the paper form's order
  const other = property.indexOf('other') !== -1 ? str(b.property_other).slice(0, PROPERTY_OTHER_MAX) : '';
  if (property.indexOf('other') !== -1 && !other) return { error: 'Say what the other company property is.' };

  return {
    reason,
    form: {
      action: 'form',
      v: FORM_VERSION,
      leave_kind: kind,
      ticket: !!ticket,
      contact_local: local || null,
      contact_home: home || null,
      local_from_record: localFromRecord,
      property,                       // [] means "None"
      property_other: other || null,
    },
  };
}

// The form entry of a request (booked_days), or null for a request raised before 19 Oct 2026.
export function formOf(bookedDays) {
  let f = null;
  (Array.isArray(bookedDays) ? bookedDays : []).forEach((b) => { if (b && b.action === 'form') f = b; });
  return f;
}

// The kind of leave in words, as the paper form names it.
const APL_LABELS = { maternity: 'Maternity', paternity: 'Paternity', compassionate: 'Compassionate', other: 'Other' };
export function leaveTitle(leaveType, form, aplReason) {
  if (leaveType === 'holiday') return form && form.leave_kind === 'emergency' ? 'Emergency leave' : 'Annual leave';
  if (leaveType === 'toil') return 'TOIL';
  if (leaveType === 'apl') return 'Authorised paid leave' + (aplReason && APL_LABELS[aplReason] ? ' (' + APL_LABELS[aplReason] + ')' : '');
  return String(leaveType || '');
}
// The property list in words: 'SIM, Laptop, Other: van keys', or 'None'.
export function propertyText(form) {
  const list = form && Array.isArray(form.property) ? form.property : [];
  if (!list.length) return 'None';
  return list.map((k) => {
    const item = PROPERTY_ITEMS.find((p) => p.key === k);
    const label = item ? item.label : k;
    return k === 'other' && form.property_other ? label + ': ' + form.property_other : label;
  }).join(', ');
}
